import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertService } from '@selfai-platform/shared';
import { MonacoConfigService } from '../../services';

@Component({
  selector: 'selfai-platform-chat-code-viewer',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonDirective, TooltipModule, MonacoEditorModule, TranslateModule],
  templateUrl: './chat-code-viewer.component.html',
  styleUrls: ['./chat-code-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatCodeViewerComponent implements OnInit {
  @Input() codeContent: string = '';
  @Input() language: string = 'sql';
  @Input() readonly: boolean = true;
  @Input() maxHeight: number = 300;
  @Input() minHeight: number = 100;

  @Output() codeChange = new EventEmitter<string>();
  @Output() openEditor = new EventEmitter<string>();

  public editorOptions: any = {};
  public editorHeight: number = 150;
  public lineCount: number = 0;

  constructor(
    private readonly monacoConfigService: MonacoConfigService,
    private readonly alertService: AlertService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.calculateHeight();
    this.setupEditorOptions();
  }

  private setupEditorOptions(): void {
    this.editorOptions = this.monacoConfigService.getEditorConfig(
      this.language,
      'display',
      { readOnly: this.readonly }
    );
  }

  public onCodeChange(value: string): void {
    if (!this.readonly) {
      this.codeChange.emit(value);
    }
  }


  private calculateHeight(): void {
    if (!this.codeContent) return;

    this.lineCount = this.codeContent.split('\n').length;
    this.editorHeight = Math.max(this.minHeight, Math.min(this.maxHeight, this.lineCount * 18 + 20));
  }

  public copyCode(): void {
    if (!this.codeContent) return;

    navigator.clipboard.writeText(this.codeContent).catch((err) => {
      this.alertService.error(this.translateService.instant('chat.errors.failed-to-copy-code') + ':', err);
      this.fallbackCopyToClipboard(this.codeContent);
    });
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (err) {
      this.alertService.error(this.translateService.instant('chat.errors.fallback-copy-failed') + ':', err);
    }

    document.body.removeChild(textArea);
  }

  public openInEditor(): void {
    this.openEditor.emit(this.codeContent);
  }
}
